import { CloudUploadOutlined, LeftOutlined, PictureOutlined } from "@ant-design/icons"
import { Alert, Button, Segmented, Skeleton } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { PortalEmptyState } from "./components/PortalEmptyState.tsx"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { PortalPageHeader } from "./components/PortalPageHeader.tsx"
import { PhotoViewer } from "./components/PhotoViewer.tsx"
import { UploadAndShareModal, type UploadProjectOption } from "./components/UploadAndShareModal.tsx"
import { resolveRecipientLabel } from "./helpers/recipient.ts"
import {
  fetchAllProjectsPhotos,
  fetchPortalAlbums,
  fetchPortalPhotos,
  uploadPhotoThunk
} from "./redux/portal.thunks.ts"
import type { PortalAlbum, PortalCrossProjectPhoto, PortalPhoto } from "./types/portal.types.ts"

type PhotoTab = "all" | "contractor" | "you" | "albums"

function AlbumCard({ album, onOpen }: { album: PortalAlbum; onOpen: () => void }) {
  const { t } = useTranslation()
  const cover = album.attachments[0]

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col overflow-hidden rounded-lg border border-gray-100 bg-white text-left shadow-sm transition hover:shadow-md"
    >
      <div className="aspect-[4/3] w-full bg-gray-100">
        {cover ? (
          <img
            src={cover.file}
            alt={album.name ?? ""}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-300">
            <PictureOutlined className="text-3xl" />
          </div>
        )}
      </div>
      <div className="px-3 py-2">
        <p className="truncate text-sm font-medium text-gray-800">{album.name}</p>
        <p className="text-xs text-gray-500">
          {t("portal.photos.photoCount", { count: album.attachments_count })}
        </p>
      </div>
    </button>
  )
}

function PhotoTile({
  photo,
  isMine,
  projectName,
  onOpen
}: {
  photo: PortalPhoto
  isMine: boolean
  projectName?: string
  onOpen: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col overflow-hidden rounded-md border border-gray-100 bg-white text-left"
    >
      <div className="aspect-square w-full overflow-hidden bg-gray-100">
        <img
          src={photo.file}
          alt={photo.original_filename}
          loading="lazy"
          className="h-full w-full object-cover transition hover:scale-105"
        />
      </div>
      <div className="px-2 py-1.5">
        <p className="truncate text-xs font-medium text-gray-700">{photo.original_filename}</p>
        <p className="truncate text-[11px] text-gray-400">
          {projectName ? `${projectName} · ` : ""}
          {isMine ? t("portal.documents.sourceYou") : (photo.created_by_name ?? t("portal.photos.fromContractor"))}
        </p>
      </div>
    </button>
  )
}

function PortalPhotosPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState<PhotoTab>("all")
  const [openAlbumId, setOpenAlbumId] = useState<string | null>(null)
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)
  const [visibleCount, setVisibleCount] = useState(24)
  const [uploadOpen, setUploadOpen] = useState(searchParams.get("upload") === "1")

  const userInfo = useAppSelector((state) => state.auth.userInfo)
  const selectedProjectId = useAppSelector(
    (state) => state.portal.selectedProjectId
  )
  const projects = useAppSelector((state) => state.portal.projects)
  const projectsLoaded = useAppSelector((state) => state.portal.projectsLoaded)
  const detail = useAppSelector((state) => state.portal.projectDetail)
  const albums = useAppSelector((state) => state.portal.albums)
  const fetching = useAppSelector((state) => state.portal.albumsFetching)
  const loaded = useAppSelector((state) => state.portal.albumsLoaded)
  const failed = useAppSelector((state) => state.portal.albumsError)
  const photos = useAppSelector((state) => state.portal.photos)
  const photosLoaded = useAppSelector((state) => state.portal.photosLoaded)
  const crossPhotos = useAppSelector((state) => state.portal.crossProjectPhotos)
  const crossFetching = useAppSelector((state) => state.portal.crossProjectPhotosFetching)
  const crossLoaded = useAppSelector((state) => state.portal.crossProjectPhotosLoaded)

  const allProjectsMode = selectedProjectId === null

  useEffect(() => {
    if (selectedProjectId) {
      void dispatch(fetchPortalAlbums(selectedProjectId))
      void dispatch(fetchPortalPhotos(selectedProjectId))
    }
  }, [dispatch, selectedProjectId])

  useEffect(() => {
    if (allProjectsMode && projectsLoaded && projects.length > 1 && !crossLoaded && !crossFetching) {
      void dispatch(
        fetchAllProjectsPhotos(projects.map((p) => ({ id: p.id, lead_name: p.lead_name })))
      )
    }
  }, [allProjectsMode, projectsLoaded, projects, crossLoaded, crossFetching, dispatch])

  useEffect(() => {
    if (searchParams.get("upload") === "1") {
      setUploadOpen(true)
      const next = new URLSearchParams(searchParams)
      next.delete("upload")
      setSearchParams(next, { replace: true })
    }
  }, [])

  useEffect(() => {
    // "Albums" only exists per-project — leaving it selected while switching
    // into All-projects would strand the tab on a view with nothing to show.
    if (allProjectsMode && tab === "albums") setTab("all")
  }, [allProjectsMode, tab])

  const sourcePhotos: (PortalPhoto | PortalCrossProjectPhoto)[] = allProjectsMode
    ? crossPhotos
    : photos

  const visiblePhotos = useMemo(() => {
    return sourcePhotos.filter((photo) => {
      const isMine = photo.created_by === userInfo?.id
      if (tab === "you") return isMine
      if (tab === "contractor") return !isMine
      return true
    })
  }, [sourcePhotos, tab, userInfo?.id])

  const projectName =
    projects.find((p) => p.id === selectedProjectId)?.lead_name ?? ""
  const recipientLabel = resolveRecipientLabel(detail?.project_managers, t)

  const uploadProjectOptions: UploadProjectOption[] = useMemo(
    () =>
      projects.map((project) => ({
        id: project.id,
        name: project.lead_name,
        recipientLabel: resolveRecipientLabel(project.project_managers, t)
      })),
    [projects, t]
  )

  const openAlbum = albums.find((album) => album.id === openAlbumId) ?? null

  useEffect(() => {
    setVisibleCount(24)
  }, [openAlbumId])

  const handleUpload = async (file: File, _note: string, projectId: string) => {
    const result = await dispatch(uploadPhotoThunk({ projectId, file }))
    return uploadPhotoThunk.fulfilled.match(result)
  }

  const handleAllUploaded = () => {
    if (selectedProjectId) {
      void dispatch(fetchPortalPhotos(selectedProjectId))
    } else {
      void dispatch(
        fetchAllProjectsPhotos(projects.map((p) => ({ id: p.id, lead_name: p.lead_name })))
      )
    }
  }

  if (!projectsLoaded) {
    return (
      <PortalPageContainer>
        <Skeleton active paragraph={{ rows: 6 }} />
      </PortalPageContainer>
    )
  }

  if (projects.length === 0) {
    return (
      <PortalPageContainer>
        <PortalPageHeader title={t("portal.photos.title")} />
        <PortalEmptyState
          title={t("portal.home.noProjectsTitle")}
          description={t("portal.home.noProjectsDescription")}
        />
      </PortalPageContainer>
    )
  }

  if (!allProjectsMode && fetching && !loaded) {
    return (
      <PortalPageContainer>
        <Skeleton active paragraph={{ rows: 6 }} />
      </PortalPageContainer>
    )
  }

  if (!allProjectsMode && failed) {
    return (
      <PortalPageContainer>
        <Alert
          type="error"
          showIcon
          message={t("portal.photos.loadError")}
          action={
            <Button
              size="small"
              onClick={() => void dispatch(fetchPortalAlbums(selectedProjectId as string))}
            >
              {t("portal.home.retry")}
            </Button>
          }
        />
      </PortalPageContainer>
    )
  }

  if (openAlbum) {
    const visibleAlbumPhotos = openAlbum.attachments.slice(0, visibleCount)
    return (
      <PortalPageContainer>
        <button
          type="button"
          onClick={() => setOpenAlbumId(null)}
          className="mb-3 flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-primary"
        >
          <LeftOutlined /> {t("portal.photos.backToAlbums")}
        </button>
        <h1 className="mb-4 text-left text-[30px] font-semibold text-gray-900">
          {openAlbum.name}
        </h1>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {visibleAlbumPhotos.map((photo, index) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => setViewerIndex(index)}
              className="aspect-square overflow-hidden rounded-md bg-gray-100"
            >
              <img
                src={photo.file}
                alt={photo.original_filename}
                loading="lazy"
                className="h-full w-full object-cover transition hover:scale-105"
              />
            </button>
          ))}
        </div>

        {visibleCount < openAlbum.attachments.length && (
          <div className="mt-4 flex justify-center">
            <Button onClick={() => setVisibleCount((count) => count + 24)}>
              {t("portal.photos.loadMore")}
            </Button>
          </div>
        )}

        {viewerIndex !== null && (
          <PhotoViewer
            photos={openAlbum.attachments}
            index={viewerIndex}
            onClose={() => setViewerIndex(null)}
            onNavigate={setViewerIndex}
          />
        )}
      </PortalPageContainer>
    )
  }

  const isLoading = allProjectsMode ? crossFetching && !crossLoaded : false
  const hasLoaded = allProjectsMode ? crossLoaded : photosLoaded
  const nothingAtAll = hasLoaded && sourcePhotos.length === 0 && (allProjectsMode || albums.length === 0)

  return (
    <PortalPageContainer>
      <PortalPageHeader
        title={t("portal.photos.title")}
        actions={
          <Button type="primary" icon={<CloudUploadOutlined />} onClick={() => setUploadOpen(true)}>
            {t("portal.upload.buttonLabel")}
          </Button>
        }
      />

      <Segmented
        className="mb-4"
        value={tab}
        onChange={(value) => setTab(value as PhotoTab)}
        options={[
          { label: t("portal.documents.sourceAll"), value: "all" },
          { label: t("portal.documents.sourceContractor"), value: "contractor" },
          { label: t("portal.documents.sourceYou"), value: "you" },
          ...(allProjectsMode ? [] : [{ label: t("portal.photos.albumsTab"), value: "albums" }])
        ]}
      />

      {isLoading && <Skeleton active paragraph={{ rows: 6 }} />}

      {nothingAtAll ? (
        <PortalEmptyState
          title={t("portal.photos.emptyTitle")}
          description={t("portal.photos.emptyDescription")}
        />
      ) : tab === "albums" && !allProjectsMode ? (
        albums.length === 0 ? (
          <PortalEmptyState title={t("portal.photos.noAlbumsYet")} />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {albums.map((album) => (
              <AlbumCard key={album.id} album={album} onOpen={() => setOpenAlbumId(album.id)} />
            ))}
          </div>
        )
      ) : visiblePhotos.length === 0 ? (
        <PortalEmptyState title={t("portal.photos.noMatches")} />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {visiblePhotos.map((photo, index) => (
            <PhotoTile
              key={photo.id}
              photo={photo}
              isMine={photo.created_by === userInfo?.id}
              projectName={allProjectsMode ? (photo as PortalCrossProjectPhoto).project_name : undefined}
              onOpen={() => setViewerIndex(index)}
            />
          ))}
        </div>
      )}

      {viewerIndex !== null && tab !== "albums" && (
        <PhotoViewer
          photos={visiblePhotos}
          index={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onNavigate={setViewerIndex}
        />
      )}

      <UploadAndShareModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        accept="image/*"
        onUpload={handleUpload}
        onAllUploaded={handleAllUploaded}
        {...(allProjectsMode
          ? { projectOptions: uploadProjectOptions }
          : { project: { id: selectedProjectId as string, name: projectName, recipientLabel } })}
      />
    </PortalPageContainer>
  )
}

export default PortalPhotosPage
